import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Info } from "lucide-react";
import { SignerData } from "../SignatureRequestWizard";

interface AddSignersStepProps {
  unaId: string;
  documentId: string;
  taskId: string;
  signers: SignerData[];
  onUpdateSigners: (signers: SignerData[]) => void;
}

interface UnaMember {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  member_role: string;
}

export default function AddSignersStep({
  unaId,
  documentId,
  taskId,
  signers,
  onUpdateSigners,
}: AddSignersStepProps) {
  const [members, setMembers] = useState<UnaMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMembers();
  }, [unaId]);

  const loadMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('una_members')
        .select('id, user_id, full_name, email, member_role')
        .eq('una_id', unaId)
        .eq('status', 'active');

      if (error) throw error;
      setMembers(data || []);
      
      // Auto-add all active members as signers if none selected
      if (signers.length === 0 && data && data.length > 0) {
        const autoSigners: SignerData[] = data.map((member, index) => ({
          id: member.id,
          name: member.full_name,
          email: member.email,
          role: member.member_role,
          signatureType: 'drawn' as const,
          unaMemberId: member.id,
          order: index + 1,
        }));
        onUpdateSigners(autoSigners);
      }
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center text-muted-foreground">Loading members...</div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          This document will be sent to all active UNA members for signature via email.
          Members will receive a secure link to sign the document.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Signers ({signers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {signers.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">
              No active members found. Add members to your UNA first.
            </div>
          ) : (
            <div className="space-y-3">
              {signers.map((signer, index) => (
                <div
                  key={signer.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{signer.name}</div>
                      <div className="text-sm text-muted-foreground">{signer.email}</div>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {signer.role?.replace(/_/g, ' ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
